import type {
  EntityDetailResponse,
  EntityFieldDefinition,
  EntityFieldDefinitionSemanticConfig,
  EntityFieldDefinitionSemanticType,
  EntityFieldDefinitionType,
  FieldParamDefinition,
} from '@moment/horizon-data-access/platform'

import { isEmptyValue, VALUE_PLACEHOLDER } from '@moment/utils/format'

/**
 * SORM scalar field types, exactly as the schema declares them.
 *
 * Aliased to the generated union rather than restated, so a type added to the
 * platform schema can't reach a projection without a branch that names it. The
 * previous hand-written list was missing both `datetime` and `object`, and the
 * cast that fed it hid that.
 *
 * `datetime` is deliberately *not* collapsed into `date`: a datetime is an
 * instant, so a calendar-day filter on one has to widen into that day's range,
 * while a `date` is the calendar day itself and must not shift.
 */
export type ScalarFieldType = EntityFieldDefinitionType

/**
 * The add-filter editor a field maps to. Structurally identical to
 * `FilterEditorKind` in `@moment/data-grid-ui` — the classification is schema
 * interpretation and lives here; the grid's editor components consume it via
 * structural typing.
 */
type SormFilterEditorKind =
  | 'categorical'
  | 'numeric'
  | 'date'
  | 'boolean'
  | 'text'

/**
 * Raw value → display label overlay for one SORM field. Not exhaustive:
 * unmapped values pass through unchanged.
 */
export type EnumMapping = Readonly<Record<string, string>>

/**
 * Display label for a raw enum value; unmapped values pass through unchanged.
 */
export function resolveEnumLabel(
  value: string,
  mapping: EnumMapping | undefined,
): string {
  return mapping?.[value] ?? value
}

/**
 * A single normalized read of one SORM schema field's capabilities — the one
 * place that interprets `type` and the `groupable`/`sortable`/`filterable`/
 * `visible` flags. Every schema projection (column catalog, filterable fields,
 * sort picker, filter type-index, colDefs, detail widgets) consumes this
 * instead of re-deriving from the raw field, so the interpretation rules live
 * in one spot.
 */
export interface FieldDescriptor {
  /** Bare field name (the root-entity column id). */
  name: string
  /** User-facing label (`display_name`, falling back to `name`). */
  label: string
  /**
   * Schema-authored field description, surfaced as the column header tooltip.
   * `undefined` when the schema omits it.
   */
  description: string | undefined
  /** SORM scalar type as the schema declares it; `datetime` stays distinct from `date`. */
  scalarType: ScalarFieldType
  /** Shown to users unless the schema explicitly flags `visible: false`. */
  displayable: boolean
  /**
   * Whether the field requires per-instance `params` to bind (e.g.
   * `saved_view_membership` needs a `view_id`). Such a field can't be projected
   * as a bare column, so it's left out of the addable catalog.
   */
  parameterized: boolean
  /** Filterable unless the schema omits/denies `filterable`. */
  filterable: boolean
  /** Groupable (a dimension/id field) per the schema. */
  groupable: boolean
  /** Sortable unless the schema explicitly flags `sortable: false`. */
  sortable: boolean
  /**
   * Whether the backend's top-level `search` includes this field in its
   * `includes_case_insensitive` expansion. The live-poll client mirror matches
   * exactly this set so it agrees with the server.
   */
  searchable: boolean
  /** Whether the Add filter's value search offers this field's enum options. */
  valueSearchable: boolean
  isNumeric: boolean
  isDate: boolean
  /**
   * The add-filter editor this field maps to, or `undefined` when it isn't
   * filterable or has no supported editor (e.g. an `object` field, or a
   * non-groupable `set`). See {@link classifyFilterEditor}.
   */
  filterEditorKind: SormFilterEditorKind | undefined
  /**
   * The scale a credit rating's filter ranges over, lowest first. A field with
   * one is offered as an ordinal range in place of its {@link filterEditorKind}.
   */
  ordinalScale: readonly string[] | undefined
  /** The span a numeric filter's range slider covers, set by the semantic type. */
  filterSliderTrack: FilterSliderTrack | undefined
  /** The unit a numeric filter's inputs show after the value, set by the semantic type. */
  filterUnit: string | undefined
  /**
   * SORM presentation annotation driving value formatting (and, later, badge /
   * chip rendering). `undefined` when the field has no semantic type.
   */
  semanticType: EntityFieldDefinitionSemanticType | undefined
  /** Type-specific rendering hints (e.g. `currency_field`, `divisor`). */
  semanticConfig: EntityFieldDefinitionSemanticConfig | undefined
  /** Raw→display labels for low-cardinality enum fields, if any. */
  enumMapping: EnumMapping | undefined
  /**
   * The raw values a filter menu should offer, in the schema's display order —
   * union them with the values a query returns rather than replacing them.
   * Distinct from {@link enumMapping}, which labels every value (including ones
   * gated off for this caller) and so must stay the source for row rendering.
   *
   * Three states, all meaningful: a non-empty array is the caller's option set;
   * an empty array is a closed domain with every value gated off, so the schema
   * offers nothing; `undefined` is an open reference set (a mapping that only
   * labels a live reference table), where the schema knows every value in the
   * system but not which belong to this caller — build that menu from queried
   * values alone.
   */
  enumOptions: readonly string[] | undefined
  /** Parameters that must be bound to project this field. */
  params: FieldParamDefinition[]
  /** The only SORM comparisons a filter on this field may use; `undefined` when unrestricted. */
  filterOperators: readonly string[] | undefined
}

export function isInstanceField(descriptor: FieldDescriptor): boolean {
  return descriptor.params.length > 0
}

/**
 * The user-facing label for a schema field: its `display_name` when present
 * and non-blank, otherwise the raw field name. Shared by the column
 * projections so the fallback rule lives in one place.
 */
export function fieldLabel(
  field: Pick<EntityFieldDefinition, 'display_name' | 'name'>,
): string {
  const displayName = field.display_name
  if (typeof displayName === 'string' && displayName.trim().length > 0) {
    return displayName
  }
  return field.name
}

interface ColumnHeaderOptions {
  name: string
  entityName: string
  entityDisplayName: string | undefined
  description: string | undefined
}

export function buildColumnHeader({
  name,
  entityName,
  entityDisplayName,
  description,
}: ColumnHeaderOptions) {
  const entity = entityDisplayName?.trim() || entityName
  const columnDescription = isEmptyValue(description)
    ? VALUE_PLACEHOLDER
    : description

  return {
    headerName: name,
    headerTooltip: `Name: ${name}\nEntity: ${entity}\nDescription: ${columnDescription}`,
  }
}

/**
 * Prefixes a schema-authored field description with its owning entity.
 * The owning entity's display name identifies joined fields whose labels may
 * otherwise be ambiguous. Falls back to the entity name when no display name
 * is available, and to the bare description when the entity is unknown.
 */
export function buildEntityFieldDescription(
  description: string | undefined,
  entity: Pick<EntityDetailResponse, 'display_name' | 'name'> | undefined,
): string | undefined {
  if (description === undefined) {
    return
  }

  const displayName = entity?.display_name
  if (typeof displayName === 'string' && displayName.trim().length > 0) {
    return `${displayName} field: ${description}`
  }

  const entityName = entity?.name
  if (typeof entityName === 'string' && entityName.trim().length > 0) {
    return `${entityName} field: ${description}`
  }

  return description
}

/**
 * The add-filter editor each (filterable) scalar type maps to. `set` and
 * `object` are excluded — see {@link classifyFilterEditor}. A `datetime` gets the
 * same day picker as a `date`; they differ in how the chosen day is encoded, not
 * in how it's picked.
 */
const EDITOR_BY_TYPE: Record<
  Exclude<ScalarFieldType, 'set' | 'object'>,
  SormFilterEditorKind
> = {
  string: 'categorical',
  int: 'numeric',
  decimal: 'numeric',
  date: 'date',
  datetime: 'date',
  bool: 'boolean',
}

/** Structurally identical to `NumericSliderTrack` in `@moment/data-grid-ui`. */
interface FilterSliderTrack {
  min: number
  max: number
  step: number
}

const FILTER_SLIDER_TRACK_BY_SEMANTIC_TYPE: Partial<
  Record<EntityFieldDefinitionSemanticType, FilterSliderTrack>
> = {
  yield: { min: 0, max: 15, step: 0.05 },
  duration_years: { min: 0, max: 30, step: 0.5 },
  price: { min: 50, max: 150, step: 0.25 },
  quantity: { min: 0, max: 1_000_000, step: 5000 },
}

function filterSliderTrack(
  semanticType: EntityFieldDefinitionSemanticType | undefined,
): FilterSliderTrack | undefined {
  if (semanticType === undefined) {
    return
  }
  return FILTER_SLIDER_TRACK_BY_SEMANTIC_TYPE[semanticType]
}

const FILTER_UNIT_BY_SEMANTIC_TYPE: Partial<
  Record<EntityFieldDefinitionSemanticType, string>
> = {
  yield: '%',
  percentage: '%',
  basis_points: 'bps',
  duration_years: 'yrs',
}

function filterUnit(
  semanticType: EntityFieldDefinitionSemanticType | undefined,
): string | undefined {
  if (semanticType === undefined) {
    return
  }
  return FILTER_UNIT_BY_SEMANTIC_TYPE[semanticType]
}

/**
 * Picks the add-filter editor for a field from its scalar type + flags, or
 * `undefined` (no filter offered) when the field can't be filtered:
 *
 * - not `filterable`, or not `inFilterMenu` (a `visible: false` field is a hidden
 *   column and shouldn't leak into the filter menu, e.g. `Custodian (raw)`,
 *   unless it requires a filter on itself);
 * - a non-groupable `set` field: sets filter by membership (`contains_any`),
 *   but only a groupable set enumerates its elements through the grouped
 *   distinct-value query the categorical picker loads options with. A
 *   non-groupable one (e.g. `account_ids`) is offered only when a definition
 *   sources its options from a related entity (`filterOptionSources`).
 * - an `object` field: a nested JSON value, with no scalar comparison to offer.
 *
 * Groupable strings are categorical (their distinct values enumerate into a
 * picker); a non-groupable string is free text — high-cardinality, so it gets
 * the typed-fragment `text` editor instead. Numeric/date/bool map to their
 * per-type editors.
 */
function classifyFilterEditor(
  scalarType: ScalarFieldType,
  filterable: boolean,
  groupable: boolean,
  inFilterMenu: boolean,
): SormFilterEditorKind | undefined {
  if (!(filterable && inFilterMenu)) {
    return
  }
  if (scalarType === 'set') {
    return groupable ? 'categorical' : undefined
  }
  if (scalarType === 'object') {
    return
  }
  if (scalarType === 'string' && !groupable) {
    return 'text'
  }
  return EDITOR_BY_TYPE[scalarType]
}

/**
 * The scale a rating field's range runs over, which SORM compares by rank. A
 * rating with no options (an open set, or every value gated off) has no scale
 * to range over.
 */
function ordinalScale(
  semanticType: EntityFieldDefinitionSemanticType | undefined,
  enumOptions: readonly string[] | undefined,
): readonly string[] | undefined {
  if (semanticType !== 'credit_rating' || enumOptions === undefined) {
    return
  }
  // SORM lists credit ratings best first; the scale runs lowest first
  return enumOptions.length === 0 ? undefined : [...enumOptions].reverse()
}

/**
 * Describes one schema field, or `undefined` when it has no usable name (the
 * only fields worth skipping). All type/flag interpretation lives here.
 */
function describeField(
  field: EntityFieldDefinition | undefined,
): FieldDescriptor | undefined {
  if (typeof field?.name !== 'string' || field.name.length === 0) {
    return
  }
  const scalarType = field.type
  const filterable = field.filterable === true
  const groupable = field.groupable === true
  const displayable = field.visible !== false
  // A field that requires a filter on itself (`Security.ask_trade_size`) is a
  // query input, not data: it has no column, but its filter is the point.
  const isFilterInput =
    field.required_filter_fields?.includes(field.name) === true
  const inFilterMenu = displayable || isFilterInput
  const parameterized = (field.params?.length ?? 0) > 0
  return {
    name: field.name,
    label: fieldLabel(field),
    description: field.description,
    scalarType,
    displayable,
    parameterized,
    filterable,
    groupable,
    sortable: field.sortable !== false,
    searchable: field.searchable === true,
    valueSearchable: field.value_searchable ?? false,
    isNumeric: scalarType === 'int' || scalarType === 'decimal',
    isDate: scalarType === 'date' || scalarType === 'datetime',
    filterEditorKind: classifyFilterEditor(
      scalarType,
      filterable,
      groupable,
      inFilterMenu,
    ),
    ordinalScale: ordinalScale(field.semantic_type, field.enum_options),
    filterSliderTrack: filterSliderTrack(field.semantic_type),
    filterUnit: filterUnit(field.semantic_type),
    semanticType: field.semantic_type,
    semanticConfig: field.semantic_config,
    enumMapping: field.enum_mapping,
    enumOptions: field.enum_options,
    params: field.params ?? [],
    filterOperators: field.filter_operators,
  }
}

/** Describes every named field in an entity schema, in declaration order. */
export function describeFields(
  schema: EntityDetailResponse | undefined,
): FieldDescriptor[] {
  const result: FieldDescriptor[] = []
  for (const field of schema?.fields ?? []) {
    const descriptor = describeField(field)
    if (descriptor !== undefined) {
      result.push(descriptor)
    }
  }
  return result
}
